/**
 * Tracks the live ECharts instance behind each panel so an export can capture
 * exactly what is on screen.
 *
 * A module-level registry rather than React state on purpose: the export
 * button and the chart it needs to photograph sit in different subtrees, and
 * threading a ref through the dashboard for something that never affects
 * rendering would put a non-visual concern into the render path.
 *
 * Only bar, line and pie register. Scalars and tables have no chart instance,
 * which is why the server renders those into a document from their rows.
 */
import type { EChartsCoreOption, ECharts } from "echarts/core";
import { chartTheme } from "./chartTheme";

interface Entry {
  chart: ECharts;
  /** The option the chart is currently drawn with, kept so it can be restored. */
  screen: EChartsCoreOption;
  /** Builds the same chart in the light theme. Called only when exporting. */
  forExport: () => EChartsCoreOption;
}

const charts = new Map<string, Entry>();

export function registerChart(
  panelId: string,
  chart: ECharts,
  screen: EChartsCoreOption,
  forExport: () => EChartsCoreOption,
): void {
  charts.set(panelId, { chart, screen, forExport });
}

export function unregisterChart(panelId: string): void {
  charts.delete(panelId);
}

export interface PanelImage {
  panelId: string;
  dataUrl: string;
}

/**
 * Captures one chart, in the light theme whatever the screen is showing.
 *
 * The exporter composites images onto a white page, so a dark chart arrived as
 * pale text on white — legible on screen, unreadable in the document. Redrawing
 * light and restoring happens inside a single task, so the browser never paints
 * the intermediate state and the reader sees nothing.
 *
 * The restore suppresses animation before re-enabling it: replacing the option
 * wholesale makes ECharts treat the series as new and play its entry animation,
 * which would show as every bar on the dashboard growing from zero the moment
 * an export finished.
 */
function capture(entry: Entry): string {
  const asImage = () =>
    entry.chart.getDataURL({
      type: "png",
      // Two times, so a chart embedded in a PDF is not visibly soft when
      // the document is printed or zoomed.
      pixelRatio: 2,
      // Charts inherit a transparent background from the page, which
      // renders as black in some PDF viewers.
      backgroundColor: "#ffffff",
    });

  if (chartTheme().name === "light") return asImage();

  entry.chart.setOption(entry.forExport(), true);
  try {
    return asImage();
  } finally {
    entry.chart.setOption({ ...entry.screen, animation: false }, true);
    if (entry.screen.animation) entry.chart.setOption({ animation: true });
  }
}

/**
 * Captures the named panels as PNG data URLs.
 *
 * Panels without a chart instance are skipped rather than reported as an
 * error: the server renders those from rows, so a missing image is expected
 * for every KPI and table on the dashboard.
 *
 * A disposed instance throws rather than returning null, so each capture is
 * guarded individually — one panel torn down mid-export must not cost the
 * whole document.
 */
export function captureCharts(panelIds: string[]): PanelImage[] {
  const images: PanelImage[] = [];
  for (const panelId of panelIds) {
    const entry = charts.get(panelId);
    if (!entry) continue;
    try {
      images.push({ panelId, dataUrl: capture(entry) });
    } catch (error) {
      console.warn(`Could not capture panel ${panelId} for export`, error);
    }
  }
  return images;
}
